import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { Brand } from '@/components/Brand';
import { PasswordField } from '@/components/PasswordField';
import { authErrorKey } from '@/features/auth/authErrors';
import { signUp } from '@/features/auth/useSession';

const MIN_PASSWORD_LENGTH = 8;

export default function SignUpPage() {
  const { t } = useTranslation(['auth', 'errors', 'common']);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // Só sinaliza divergência depois que a pessoa começou a digitar a segunda
  // senha. Marcar campo vazio como errado é acusar quem ainda nem terminou.
  const mismatch = confirmation.length > 0 && password !== confirmation;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorKey('auth:field.passwordTooShort');
      return;
    }
    if (password !== confirmation) {
      setErrorKey('auth:field.passwordMismatch');
      return;
    }

    setErrorKey(null);
    setBusy(true);
    try {
      await signUp(email.trim(), password, displayName.trim());
      setSent(true);
    } catch (error) {
      setErrorKey(authErrorKey(error));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <main className="mh-shell justify-center px-5 py-10">
        <div className="mh-card">
          <h1 className="text-2xl">{t('auth:signUp.sent', { email })}</h1>
          <p className="mt-2 text-(--color-sand-700)">{t('auth:signUp.sentHint')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mh-shell justify-center px-5 py-10">
      <Brand />
      <h1 className="text-3xl">{t('auth:signUp.title')}</h1>
      <p className="mt-1 mb-8 text-(--color-sand-700)">{t('auth:signUp.subtitle')}</p>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="mh-field">
          <label htmlFor="displayName">{t('auth:signUp.displayName')}</label>
          <input
            id="displayName"
            type="text"
            autoComplete="nickname"
            required
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>

        <div className="mh-field">
          <label htmlFor="email">{t('auth:signIn.email')}</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <PasswordField
          id="password"
          label={t('auth:signIn.password')}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
        />

        <div>
          <PasswordField
            id="confirmation"
            label={t('auth:signUp.confirmPassword')}
            value={confirmation}
            onChange={setConfirmation}
            autoComplete="new-password"
            invalid={mismatch}
            describedBy={mismatch ? 'confirmation-error' : undefined}
          />
          {mismatch ? (
            <p
              id="confirmation-error"
              role="alert"
              className="mt-1.5 text-sm font-medium text-(--color-danger)"
            >
              {t('auth:field.passwordMismatch')}
            </p>
          ) : null}
        </div>

        {errorKey ? (
          <Alert tone="error" live="assertive">
            {t(errorKey)}
          </Alert>
        ) : null}

        <button
          type="submit"
          className="mh-button mh-button--primary mt-2"
          disabled={busy || mismatch}
        >
          {busy ? t('common:state.loading') : t('auth:signUp.submit')}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-(--color-sand-700)">
        {t('auth:signUp.hasAccount')}{' '}
        <Link to="/sign-in" className="font-semibold text-(--color-amber-700)">
          {t('auth:signUp.signInLink')}
        </Link>
      </p>
    </main>
  );
}
